<template>
  <div
    v-if="isAuthor && (isMessagePendingModeration || isEscalatedMessage || isBlockedMessage ||isDeletedMessage)"
    :id="`message-${messageId}`"
    style="display: flex; align-items: center; flex-wrap: nowrap"
  >
    <MessageFlags :message-id="messageId" />
    <a
      v-if="isDeletedMessage"
      style="cursor: pointer; text-decoration: none"
      @click="showDeletedMessage=!showDeletedMessage"
    >
      <span v-if="!showDeletedMessage" style="color: #6b7280">
        View Message
      </span>
      <span v-else style="color: #6b7280">
        Hide Message
      </span>
    </a>
  </div>
  <div v-if="isEditing || isEditSaving" style="display: flex; width: 100%">
    <div style="display: flex; flex-direction: column; width: 100%">
      <iEditor
        v-if="formattedSegments.length"
        ref="message-editor"
        :preview-link-data="previewLinkData"
        :upload-post-image="(file, blobUrl) => uploadMessageImage(file, blobUrl, messageId)"
        :segments="formattedSegments"
        hide-send-button
        variant="slack"
        :at-symbol-items="filteredAtSymbolItems"
        :loading-at-symbol-items="loadingAtSymbolItems"
        @handle-link-modal-open="$event.openModal(true)"
        @submit="saveEdit"
        @keydown.esc="stopEditingMessage(messageId)"
        @input="commentEdit = $event"
        @get:link-preview="getLinkPreviewData"
        @search:at-symbol="handleSearchMention($event.text, context, entityId)"
        @select:at-symbol="handleSelectMention"
        @remove-image="removeImageFromPendingState($event)"
      />
      <iEditor
        v-else
        ref="message-editor"
        v-model="commentEdit"
        name="editComment"
        width="fill"
        focus
        placeholder="Edit your comment"
        @keydown.esc="stopEditingMessage(messageId)"
        @enter="saveEdit"
      />
      <div style="display: flex; justify-content: flex-end; align-items: center; gap: 8px; margin-top: 8px">
        <button
          style="padding: 8px 16px; background: transparent; border: 1px solid #ddd; border-radius: 8px; cursor: pointer"
          @click="stopEditingMessage(messageId)"
        >
          Cancel
        </button>
        <button
          style="padding: 8px 16px; background: black; color: white; border: none; border-radius: 8px; cursor: pointer; position: relative"
          :disabled="isEditSaving"
          @click="saveEdit"
        >
          <span v-if="isEditSaving" style="opacity: 0.7">Saving...</span>
          <span v-else>Save</span>
        </button>
      </div>
    </div>
  </div>
  <template v-else>
    <div
      v-if="!isDeletedMessage || (isDeletedMessage && showDeletedMessage)"
      class="message-container"
      style="display: flex; width: 100%"
    >
      <iMessageSegmentsPreview
        v-if="formattedSegments.length"
        :preview-link-data="previewLinkData"
        :segments="formattedSegments"
        :author-name="authorName"
        :author-avatar="authorAvatar"
        :created-at="createdAt"
        variant="slack"
        @get:link-preview="handleGetLinkPreviewData"
        @click:at-symbol="handleAtSymbolClick"
      />
      <span v-else class="message-content">
        {{ message.messageId }} - {{ message.message }}
      </span>
    </div>
    <ModerationInfo :message-id="messageId" />
    <span v-if="isMessageLoading[messageId]?.error" style="color: #6b7280">
      {{ isMessageLoading[messageId].error }}
    </span>
  </template>
</template>

<script>
import MessageFlags from "%/apps/conversations/components/message/MessageFlags.vue";
import { mapActions, mapWritableState } from "pinia";
import { useMessageStore } from "%/apps/conversations/stores/message-store";
import dayjs from "dayjs";
import { MESSAGE_ACTIONS } from "%/apps/conversations/constants/message-action-constants";
import ModerationInfo from "%/apps/conversations/components/message/ModerationInfo.vue";
import { useGeneralStore } from "%/stores/general-store";
import { DEFAULT_USER_AVATAR } from "%/constants/user-constants";

export default {
  name: "Message",
  components: {
    ModerationInfo,
    MessageFlags,
  },
  props: {
    messageId: {
      type: Number,
      required: true,
    },
    context: {
      type: String,
      required: true,
    },
    entityId: {
      type: Number,
      required: true,
    },
  },
  emits: ["toggle:reaction-picker", "open:reply-modal", "open:profile"],
  data() {
    return {
      showDeletedMessage: false,
      previewLinkData: null,
      commentEdit: "",
    };
  },
  computed: {
    ...mapWritableState(useMessageStore, [
      "atSymbolItems",
      "filteredAtSymbolItems",
      "loadingAtSymbolItems",
    ]),
    moderationStatus() {
      const message = this.getMessage(this.messageId);
      return message?.moderationStatus || null;
    },
    createdAt() {
      return dayjs.unix(this.message.timestampCreated).format("hh:mm A");
    },
    authorName() {
      if (this.isAuthor) {
        return useGeneralStore().userData.first_name;
      }
      return this.message.authorName;
    },
    isAuthor() {
      return this.isMessageAuthor(this.messageId);
    },
    authorAvatar() {
      // if (this.avatarUrl) {
      //   return this.avatarUrl;
      // }
      if (this.isAuthor) {
        return useGeneralStore().userData.avatar_url;
      }
      return DEFAULT_USER_AVATAR;
    },
    message() {
      return this.getMessage(this.messageId);
    },
    formattedSegments() {
      // legacy messages do not have segments
      if (!this.message.segments || typeof this.message.segments !== "object") {
        return [];
      }

      if (this.message.segments.length === 0) {
        return this.message.segments;
      }

      const hasMentions = this.message.segments.some(segment => {
        const isRichText = segment.segment_type === "rich_text";
        if (!isRichText) {
          return false;
        }
        return segment.segment_content.ranges.some(range => range.style === "AT_SYMBOL");
      });

      if (!hasMentions) {
        return this.message.segments;
      }

      const myUserId = useGeneralStore().userData.user_id;

      return this.message.segments.map(segment => {
        if (segment.segment_type !== "rich_text" || !segment.segment_content?.ranges?.length) {
          return segment;
        }

        const updatedRanges = segment.segment_content.ranges.map(range => {
          if (range.style === "AT_SYMBOL") {
            return {
              ...range,
              isMe: Number.parseInt(range.entityId) === myUserId,
            };
          }
          return range;
        });

        return {
          ...segment,
          segment_content: {
            ...segment.segment_content,
            ranges: updatedRanges,
          },
        };
      });
    },
    isDeletedMessage() {
      return this.message.status === "inactive";
    },
    isEscalatedMessage() {
      return this.moderationStatus === "escalated";
    },
    isBlockedMessage() {
      return this.moderationStatus === "blocked";
    },
    isMessagePendingModeration() {
      return this.moderationStatus === "unknown";
    },
    isEditing() {
      return this.message.isBeingEdited;
    },
    isEditSaving() {
      return this.isMessageActionLoading(this.messageId, MESSAGE_ACTIONS.EDIT);
    },
    isMessageLoading() {
      const messageStore = useMessageStore();
      return messageStore.messageActionLoading || {};
    },
  },
  watch: {
    isEditing(value) {
      if (value) {
        this.$nextTick(() => {
          const ref = this.$refs["message-editor"];
          if (ref && ref.editorInstance?.commands) {
            ref.editorInstance?.commands?.focus();
            ref.editorInstance?.commands?.placeCursorAtEnd();
          }
        });
      }
    },
  },
  methods: {
    ...mapActions(useMessageStore, [
      "getMessage",
      "isMessageActionLoading",
      "isMessageAuthor",
      "editMessage",
      "startEditingMessage",
      "stopEditingMessage",
      "getMessageActionError",
      "getLinkPreviewData",
      "uploadMessageImage",
      "removeImageFromPendingState",
      "handleSearchMention",
      "handleSelectMention",
    ]),
    handleGetLinkPreviewData(url) {
      if (!url) {
        this.previewLinkData = null;
        return;
      }

      if (dayjs.unix(this.message.timestampCreated).isBefore(dayjs().subtract(5, "minute"))) {
        this.previewLinkData = null;
        return;
      }

      this.getLinkPreviewData(url, data => {
        this.previewLinkData = data;
      });
    },
    saveEdit() {
      if (!this.commentEdit) {
        return;
      }
      this.editMessage(this.messageId, this.commentEdit);
    },
    handleAtSymbolClick(entityId) {
      this.$emit("open:profile", entityId);
    },
  },
  styleGuide: () => ({
    messageContainerBackgroundHover: { "color.background": "light" },
    messageContainerBackground: { "color.background": "paper" },
    borderRadius: { "size.borderRadius": "large" },
    actionBackground: { "color.background": "paper" },
    actionBorderColor: { "color.border": "dark" },
    actionPadding: { "size.spacing": "extraSmall" },
    replyPadding: { "size.spacing": "standard" },
    containerPadding: { "size.spacing": "extraSmall" },
    modalBorderWidth: { "size.border": "thin" },
    modalBorderColor: { "color.border": "dark" },
    unseenMentionBackground: { "color.font": "error" },
  }),
};
</script>

<style lang="scss" scoped>
:deep(.i-text p) {
  margin: 0;
}

.message-content {
  word-break: break-word;
}

.message-container {
  margin-top: -2px !important;
}
</style>

